import { isNil } from 'lodash';
import { Component } from 'react';
import { DefaultTheme, ThemeProps, withTheme } from 'styled-components';
import { KeyValue } from '@mews/enums';
import { ANIMATION_DURATION, getSpacingToken } from '@optimus-web/common';
import { ClickAwayProps, ScrollAwayProps, withClickAway, withScrollAway } from '@optimus-web/helpers';
import { IconName } from '@optimus-web/icons';
import { OpenableOverlay } from '../OpenableOverlay';
import { TextInput, TextInputProps } from '../TextInput';
import { isDescendant } from '../utils/dom';
import { StructuredTime, TimeService } from '../utils/services';
import { TimePickerContainerElement } from './style';
import { TimePickerDropdown } from './TimePickerDropdown';

export interface TimePickerFieldOwnProps<T = StructuredTime> extends Omit<TextInputProps, 'onChange'> {
    getTimeService: () => TimeService<T>;
    onChange?: (value: string) => void;
    onFocus?: (event?: React.FocusEvent<HTMLInputElement>) => void;
    onBlur?: (event?: React.FocusEvent<HTMLInputElement>) => void;
    onTimeChange?: (time: T) => void;
    showSeconds?: boolean;
    value?: string;
    showAsDialog?: boolean;
    shouldNormalizeMeridiem?: boolean;
    accessibleIconLabel?: React.ReactChild;
}

type TimePickerProps<T = StructuredTime> = TimePickerFieldOwnProps<T> &
    ClickAwayProps &
    ScrollAwayProps &
    ThemeProps<DefaultTheme>;

type TimePickerFieldState = $TSFixMe;

class TimePickerComponent<T> extends Component<TimePickerProps<T>, TimePickerFieldState> {
    containerElement: $TSFixMe;
    inputElement: $TSFixMe;
    isUnmounted: $TSFixMe;
    overlayElement: $TSFixMe;

    constructor(props: TimePickerProps<T>) {
        super(props);

        let value = '';
        if (props.value) {
            ({ value } = props);
        } else if ((props as $TSFixMe).timeValue) {
            // support setting the value from time object for legacy code
            value = props.showSeconds
                ? this.props.getTimeService().getString((props as $TSFixMe).timeValue)
                : this.props.getTimeService().getShortString((props as $TSFixMe).timeValue);
        }

        this.state = {
            value,
            opened: false,
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps: $TSFixMe) {
        if (nextProps.value !== this.props.value) {
            // shitty angular is shitty angular, it passes nullified values, which would break the component
            const value = isNil(nextProps.value) ? '' : nextProps.value;
            this.setState({ value });
        }

        if (this.state.opened && nextProps.showAsDialog !== this.props.showAsDialog) {
            this.close();
        }
    }

    componentWillUnmount() {
        this.isUnmounted = true;
        this.deregisterBlurHandler();
    }

    getTimeFromValue(value: string) {
        const { showSeconds, getTimeService } = this.props;

        let time;
        if (showSeconds) {
            if (getTimeService().isValidString(value)) {
                time = getTimeService().fromString(value);
            }
        } else if (getTimeService().isValidShortString(value)) {
            time = getTimeService().fromShortString(value);
        }

        return time;
    }

    normalizeMeridiem(value: string, time: T) {
        const timeService = this.props.getTimeService();
        const meridiemLabels = timeService.getMeridiemLabels();
        const hours = timeService.getHours(time);
        const meridiem = meridiemLabels[Math.floor(hours / 12)];
        const hasMeridiemFirst = timeService.getMeridiemPosition() === 0;

        // some cultures use different whitespace characters (e.g. non-breaking space)
        const separator = /\s/.exec(value)?.[0] ?? ' ';
        const [firstPart, secondPart] = value.split(separator, 2);

        return hasMeridiemFirst ? `${meridiem}${separator}${secondPart}` : `${firstPart}${separator}${meridiem}`;
    }

    handleFocus = (e: $TSFixMe) => {
        if (!this.state.opened && !this.props.readOnly) {
            this.open();
        }

        this.props.onFocus?.(e);

        if (this.props.showAsDialog) {
            this.inputElement.blur();
        }
    };

    handleBlur = () => {
        if (this.isUnmounted || this.props.showAsDialog) {
            return;
        }

        const shouldClose = !(
            isDescendant(document.activeElement, this.containerElement) ||
            isDescendant(document.activeElement, this.overlayElement)
        );
        if (shouldClose) {
            if (this.state.opened) {
                this.close();
            }

            this.props.onBlur?.();
        }
    };

    handleChange = (e: $TSFixMe) => {
        const value = e.target ? e.target.value : e;

        this.setState({ value });
        this.props.onChange?.(value);

        // Provided for legacy code that works with instances rather than with strings
        const time = this.getTimeFromValue(value);
        if (value) {
            this.props.onTimeChange?.(time as T);
        }
    };

    handleTimeChange = (time: $TSFixMe) => {
        const value = this.props.showSeconds
            ? this.props.getTimeService().getString(time)
            : this.props.getTimeService().getShortString(time);

        /**
         * Normalization needs to happen because of mismatch between frontend (Intl) and backend (.NET) cultures.
         * In some cultures, meridiem values differ in those 2 sources and creates discrepancies in Metas-based forms.
         */
        let finalValue = value;
        if (this.props.shouldNormalizeMeridiem && !this.props.getTimeService().has24HourFormat()) {
            finalValue = this.normalizeMeridiem(value, time);
        }

        this.setState({ value: finalValue });
        this.props.onChange?.(finalValue);
        this.props.onTimeChange?.(time);
        this.close();
    };

    handleAwayEvent = () => {
        if (this.state.opened) {
            this.close();
            this.props.onBlur?.();
        }
    };

    handleArrowClick = () => {
        if (!this.state.opened) {
            this.inputElement.focus();
        }
    };

    handleKeyDown = (e: $TSFixMe) => {
        let handled;
        switch (e.key) {
            case KeyValue.ArrowUp:
                this.addMinutes(-60);
                handled = true;
                break;

            case KeyValue.ArrowDown:
                this.addMinutes(60);
                handled = true;
                break;

            case KeyValue.ArrowLeft:
                this.addMinutes(-15);
                handled = true;
                break;

            case KeyValue.ArrowRight:
                this.addMinutes(15);
                handled = true;
                break;

            default:
                break;
        }

        if (handled) {
            e.stopPropagation();
            e.preventDefault();
        }
    };

    addMinutes(minutes: $TSFixMe) {
        const time = this.getTimeFromValue(this.state.value);
        if (time) {
            const newTime = this.props.getTimeService().addMinutes(time, minutes);
            const value = this.props.getTimeService().getShortString(newTime);

            this.setState({ value });
            this.props.onChange?.(value);
            this.props.onTimeChange?.(newTime);
        }
    }

    blurHandler = () => this.handleBlur();

    registerBlurHandler() {
        document.addEventListener('focus', this.blurHandler, true);
    }

    deregisterBlurHandler() {
        document.removeEventListener('focus', this.blurHandler);
    }

    open() {
        if (!this.state.opened && !this.props.readOnly) {
            this.setState({ opened: true });
            if (!this.props.showAsDialog) {
                window.setTimeout(() => {
                    const options = {
                        handler: this.handleAwayEvent,
                        roots: [() => this.containerElement, () => this.overlayElement],
                    };
                    this.props.registerClickAway(options);
                    this.props.registerScrollAway(options);
                }, ANIMATION_DURATION.fast);
            }
            this.registerBlurHandler();
        }
    }

    close() {
        if (this.state.opened) {
            this.setState({ opened: false });
            if (!this.props.showAsDialog) {
                this.props.deregisterClickAway();
                this.props.deregisterScrollAway();
            }
            this.deregisterBlurHandler();
        }
    }

    innerRefFunction = (c: $TSFixMe) => {
        this.containerElement = c;
        if ((this.props as $TSFixMe).innerRef) {
            (this.props as $TSFixMe).innerRef(c);
        }
    };

    handlerOverlayRef = (c: $TSFixMe) => (this.overlayElement = c);

    render() {
        const {
            className,
            getTimeService,
            showSeconds,
            theme,

            // omitted props
            onTimeChange,

            ...other
        } = this.props;
        const { opened, value } = this.state;

        const time = this.getTimeFromValue(value);

        const containerProps = {
            className,
            ref: this.innerRefFunction,
        };

        const inputProps = {
            ...other,
            ref: (c: $TSFixMe) => (this.inputElement = c),
            onChange: this.handleChange,
            onFocus: this.handleFocus,
            onKeyDown: this.handleKeyDown,
            value,
            interactiveIconHandler: this.handleArrowClick,
            interactiveIcon: IconName.Time,
            accessibleIconLabel: this.props.accessibleIconLabel,
        };

        const openableOverlayProps = {
            opened,
            anchorElement: () => this.containerElement,
            innerRef: this.handlerOverlayRef,
            offset: getSpacingToken('50')({ theme }),
        };

        const timePickerProps = {
            ...other,
            showSeconds,
            getTimeService,
            onTimeChange: this.handleTimeChange,
            time,
        };

        return (
            <TimePickerContainerElement {...containerProps}>
                <TextInput {...inputProps} />
                <OpenableOverlay {...openableOverlayProps}>
                    {/* @ts-expect-error $TSFixMeError: Type '{ showSeconds: boolean | undefined; getTimeService: $TSFixMeFunction; onTimeChange: (time: any) => void; time: any... */}
                    <TimePickerDropdown {...timePickerProps} />
                </OpenableOverlay>
            </TimePickerContainerElement>
        );
    }
}

export const TimePicker = withTheme(withScrollAway(withClickAway(TimePickerComponent))) as TimePickerFieldType;

type TimePickerFieldType = <T>(props: TimePickerFieldOwnProps<T>) => React.ReactElement<TimePickerFieldOwnProps<T>>;
